import React__default, { useState, useEffect } from 'react';
import { NvInput } from '../NvInput/component.js';
import { NvButton } from '../NvButton/component.js';
import { NvCheckbox } from '../NvCheckbox/component.js';
import { NvLoader } from '../NvLoader/component.js';
import styles from './styles.module.scss.js';
import authenticate from '../../../helpers/authenticate.js';
import storage from '../../../helpers/storage.js';

/**
 * @description Nimvelo Login component
 * @props applicationName, logoSrc, onSubmit, localStorageKey, dev, onToken, className, containerColor, formColor,
 * signUpLink, forgotPasswordLink, error, inverted, inputComponent, passwordComponent
 * */
const NvLogin = ({ apiBase, disabled, applicationName, title: titleProp, upperTitle, logoSrc, onPreSubmit = () => Promise.resolve(), onSubmit = authenticate, localStorageKey, dev = false, onToken = () => { }, className, containerColor, formColor, signUpLink, hideRememberMe, forgotPasswordLink, error, inverted = false, submitButtonText, loginRedirectUrl, partnerId, rurl, usernameComponent, passwordComponent, }) => {
    const tokenKey = `${localStorageKey}-token`;
    const expiresKey = `${tokenKey}-expires`;
    const rememberMeKey = `${tokenKey}-remember`;
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [hasUsername, setHasUsername] = useState(true);
    const [hasPassword, setHasPassword] = useState(true);
    const [loginError, setLoginError] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(!hideRememberMe && (!!storage.get(rememberMeKey) || false));
    const [loginAttempted, setLoginAttempted] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isTokenSet, setIsTokenSet] = useState(false);
    const [showForm, setShowForm] = useState(false);
    useEffect(() => {
        if (hideRememberMe) {
            // we are't checking for previous logins, show login form
            setShowForm(true);
            return;
        }
        const token = storage.get(tokenKey);
        const storedExpires = storage.get(expiresKey);
        const now = Date.now();
        const isTokenValid = storedExpires && Date.parse(storedExpires) >= now;
        if (!token || !isTokenValid) {
            if (loginAttempted) {
                if (loginRedirectUrl) {
                    window.location.href =
                        loginRedirectUrl + (rurl ? `?rurl=${rurl}` : '');
                }
                setShowForm(true);
            }
            else if (!isLoggingIn) {
                setIsLoggingIn(true);
                onSubmit('', '', dev, {
                    apiBase,
                    cookieAuth: true,
                    partnerId,
                }).then(response => {
                    //  Authentication successful
                    if (typeof response === 'object') {
                        const data = Object.assign(response);
                        const { accessToken, expires } = data;
                        //  set localstorage if rememberme is checked
                        storage.set(tokenKey, accessToken);
                        storage.set(expiresKey, expires);
                    }
                    else {
                        storage.set(tokenKey, '');
                        storage.set(expiresKey, '');
                    }
                    setLoginAttempted(true);
                });
            }
            return;
        }
        if (token && storedExpires) {
            if (!isTokenValid) {
                // Token has expired
                storage.set(tokenKey, '');
                storage.set(expiresKey, '');
                setShowForm(true);
            }
            else if (!isTokenSet) {
                // Token is valid, call onToken straight away
                onToken(token, storedExpires, rememberMe);
                setIsTokenSet(true);
            }
        }
    });
    const title = titleProp || 'Log In';
    const submit = () => {
        //  clear login error
        setLoginError('');
        //  show error if username or password is empty
        setHasUsername(!!username);
        setHasPassword(!!password);
        //  user has entered username and password
        if (username && password) {
            //  set loading elements
            const timeouts = [
                setTimeout(() => {
                    setLoginError("It's taking a bit longer than usual to log you in.");
                }, 3000),
                setTimeout(() => {
                    setLoginError('This is taking a very long time, please check your internet connection.');
                }, 10000),
            ];
            setLoading(true);
            onPreSubmit(username, password)
                .then(res => {
                if (res) {
                    // Error message
                    return res;
                }
                return onSubmit(username, password, dev, {
                    apiBase,
                    cookieAuth: !!loginRedirectUrl,
                    partnerId,
                });
            })
                .then(response => {
                //  clear loading elements once fetch is finished
                timeouts.forEach(clearTimeout);
                setLoading(false);
                //  Authentication successful
                if (typeof response === 'object') {
                    // console.log('%cLogin success', 'color:green');
                    const data = Object.assign(response);
                    const { accessToken, expires } = data;
                    //  set localstorage if rememberme is checked
                    storage.set(tokenKey, !hideRememberMe && rememberMe ? accessToken : '');
                    storage.set(expiresKey, !hideRememberMe && rememberMe ? expires : '');
                    storage.set(rememberMeKey, !hideRememberMe && rememberMe);
                    //  if implementor decides to use token, they can override onToken and use the generated token
                    onToken(accessToken, expires, rememberMe);
                }
                else {
                    storage.set(tokenKey, '');
                    storage.set(expiresKey, '');
                    storage.set(rememberMeKey, !hideRememberMe && rememberMe);
                    if (response.toLowerCase().includes('failed to fetch')) {
                        //  Authentication failed
                        setLoginError('Unable to connect :( Please check your internet connection and try again');
                    }
                    else {
                        setLoginError(response);
                    }
                }
            });
        }
    };
    const handleKeyDown = (e) => {
        // handle if enter key is pressed then submit
        if (e.keyCode === 13) {
            submit();
        }
    };
    if (loginRedirectUrl) {
        return React__default.createElement(React__default.Fragment, null);
    }
    if (!showForm) {
        return React__default.createElement(React__default.Fragment, null);
    }
    return (React__default.createElement("div", { className: `${styles.container} ${className && className} ${inverted && styles.inverted}`, style: { backgroundColor: containerColor || '' } },
        React__default.createElement("div", { className: styles.form, style: { backgroundColor: formColor || '' } },
            logoSrc && (React__default.createElement("div", { className: styles.logo_container },
                React__default.createElement("img", { className: styles.logo, src: logoSrc, alt: "logo" }))),
            upperTitle && React__default.createElement("div", { className: styles.upperTitle }, upperTitle),
            React__default.createElement("div", { className: styles.input_container },
                React__default.createElement("h1", { className: styles.title },
                    title,
                    applicationName && ` To ${applicationName}`),
                usernameComponent?.({
                    disabled,
                    username,
                    hasUsername,
                    onChange: u => {
                        setUsername(u);
                        setHasUsername(!!u);
                    },
                    onKeyDown: handleKeyDown,
                }) || (React__default.createElement(NvInput, { disabled: disabled, labelAlign: "center", labelText: "Username", labelPosition: "top", onChange: (e) => {
                        setUsername(e.target.value);
                        setHasUsername(!!e.target.value);
                    }, value: username, validationError: hasUsername ? '' : 'Enter Username', reserveErrorSpace: true, 
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus: true, inputProps: { onKeyDown: handleKeyDown } })),
                passwordComponent?.({
                    disabled,
                    password,
                    hasPassword,
                    onChange: pw => {
                        setPassword(pw);
                        setHasPassword(!!pw);
                    },
                    onKeyDown: handleKeyDown,
                }) || (React__default.createElement(NvInput, { disabled: disabled, labelAlign: "center", labelText: "Password", labelPosition: "top", type: "password", onChange: (e) => {
                        setPassword(e.target.value);
                        setHasPassword(!!e.target.value);
                    }, value: password, validationError: hasPassword ? '' : 'Enter Password', reserveErrorSpace: true, inputProps: { onKeyDown: handleKeyDown } })),
                React__default.createElement("div", { className: styles.login_options },
                    !hideRememberMe && (React__default.createElement(NvCheckbox, { disabled: disabled, labelText: "Remember Me", labelPosition: "right", onChange: () => {
                            setRememberMe(!rememberMe);
                        }, checked: rememberMe })),
                    forgotPasswordLink && (React__default.createElement("a", { className: styles.forgot_password, href: forgotPasswordLink }, "Forgot Password?"))),
                React__default.createElement(NvButton, { disabled: disabled, onClick: submit, fullWidth: true }, submitButtonText || 'LOG IN'),
                React__default.createElement("span", { className: styles.login_error },
                    React__default.createElement(NvLoader, { loading: isLoading, size: "small" }),
                    loginError,
                    React__default.createElement("br", null),
                    error),
                signUpLink && (React__default.createElement("span", { className: styles.signup_container },
                    "Don't have an account?",
                    React__default.createElement("a", { className: styles.signup_link, href: signUpLink },
                        ' ',
                        "Sign up!")))))));
};

export { NvLogin, NvLogin as default };
//# sourceMappingURL=component.js.map
